import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Copy, AlertTriangle, Shield } from 'lucide-react';

import { submitEthicsReport } from '../../services/ethicsReportService';
import { getEthicsReportCategories } from '../../services/ethicsReportService';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput';
import GenericSelect from '../../components/ui/GenericSelect';
import CheckboxInput from '../../components/ui/CheckboxInput';

const Container = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const Header = styled.h2`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 24px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const AnonymousInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 4px;
  background: rgba(2, 145, 239, 0.06);
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  margin-top: 8px;
`;

const SuccessCard = styled(Card)`
  border: 2px solid #10B981;
  text-align: center;
`;

const WarningBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 4px;
  background: #FEF3C7;
  color: #92400E;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  margin-bottom: 20px;
`;

const CodeBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.bgColor};
  margin-bottom: 12px;
`;

const CodeLabel = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: #64748b;
  display: block;
  margin-bottom: 4px;
`;

const CodeValue = styled.span`
  font-family: monospace;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  letter-spacing: 2px;
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.primary.main};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  &:hover { background: rgba(2, 145, 239, 0.08); }
`;

const SuccessIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #D1FAE5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
`;

const SuccessTitle = styled.h3`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
`;

export default function EthicsReportPage() {
  const { t } = useTranslation();

  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    getEthicsReportCategories({ pageSize: 100 }).then((res) => {
      const data = res.data?.data;
      const items = Array.isArray(data) ? data : (data?.data || []);
      setCategories(items.map((c) => ({ id: c.id, name: t(`ethicsReport.category_${c.code}`, { defaultValue: c.name }) })));
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!categoryId || !title.trim() || !description.trim()) return;
    setSaving(true);
    try {
      const res = await submitEthicsReport({ categoryId, title, description, isAnonymous });
      const data = res.data?.data;
      if (data) {
        setResult({ trackingCode: data.trackingCode, accessPasscode: data.accessPasscode });
      }
    } catch {
      // error handled by interceptor
    } finally {
      setSaving(false);
    }
  }, [categoryId, title, description, isAnonymous]);

  const handleCopy = useCallback((text) => {
    navigator.clipboard.writeText(text);
  }, []);

  if (result) {
    return (
      <Container>
        <SuccessCard>
          <SuccessIcon>
            <Shield size={28} color="#10B981" />
          </SuccessIcon>
          <SuccessTitle>{t('ethicsReport.submitSuccess')}</SuccessTitle>

          <WarningBox>
            <AlertTriangle size={18} />
            {t('ethicsReport.saveCodesWarning')}
          </WarningBox>

          <CodeBox>
            <div>
              <CodeLabel>{t('ethicsReport.trackingCode')}</CodeLabel>
              <CodeValue>{result.trackingCode}</CodeValue>
            </div>
            <CopyButton onClick={() => handleCopy(result.trackingCode)}>
              <Copy size={14} /> {t('common.copy')}
            </CopyButton>
          </CodeBox>

          <CodeBox>
            <div>
              <CodeLabel>{t('ethicsReport.accessPasscode')}</CodeLabel>
              <CodeValue>{result.accessPasscode}</CodeValue>
            </div>
            <CopyButton onClick={() => handleCopy(result.accessPasscode)}>
              <Copy size={14} /> {t('common.copy')}
            </CopyButton>
          </CodeBox>
        </SuccessCard>
      </Container>
    );
  }

  return (
    <Container>
      <Header>{t('ethicsReport.newReport')}</Header>

      <Card>
        <FieldGroup>
          <GenericSelect
            label={t('ethicsReport.category')}
            value={categoryId}
            onChange={(val) => setCategoryId(val)}
            options={categories}
            idKey="id"
            labelKey="name"
            required
          />
        </FieldGroup>

        <FieldGroup>
          <TextInput
            label={t('ethicsReport.reportTitle')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </FieldGroup>

        <FieldGroup>
          <TextInput
            label={t('ethicsReport.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            type="textarea"
            rows={5}
            required
          />
        </FieldGroup>

        <FieldGroup>
          <CheckboxInput
            label={t('ethicsReport.anonymous')}
            checked={isAnonymous}
            onChange={setIsAnonymous}
          />
          {isAnonymous && (
            <AnonymousInfo>
              <Shield size={16} />
              {t('ethicsReport.anonymousDesc')}
            </AnonymousInfo>
          )}
        </FieldGroup>

        <Button
          onClick={handleSubmit}
          disabled={!categoryId || !title.trim() || !description.trim() || saving}
        >
          {saving ? '...' : t('ethicsReport.submit')}
        </Button>
      </Card>
    </Container>
  );
}
